/**
 * Prop collision sizes.
 *
 * ── Why this lives in the sim and not with the models ────────────────────
 *
 * A prop is two things: a box the simulation collides against, and a model the
 * client draws inside that box. The model is client art and belongs there. The
 * BOX is map geometry, and map geometry has exactly one owner — `createArena`,
 * which both runtimes call so that the arena the client predicts against is the
 * arena the server resolves hits in.
 *
 * These sizes started out beside the models in the client, which was fine while
 * only a mockup placed props. The moment the server authors an arena containing
 * a desk, the server needs to know how big a desk is, and a size table the
 * server cannot see is a client and server disagreeing about where the walls
 * are. So the numbers moved here and the art imports them.
 *
 * ── Height is the surface you land on ────────────────────────────────────
 *
 * NOT the top of the model. A desk is 0.82 because that is the desk; its
 * monitors stand above the box and are scenery. Sizing the box to the tallest
 * pixel instead would leave a player standing on an invisible shelf a metre
 * above the desk they can see.
 *
 * The corollary binds the art: anything a model puts above its box must be
 * something nobody would expect to stand on. Monitors and leaves qualify; a
 * tabletop does not.
 */
/** Full extents — width, height, depth — of each prop's collision box. */
export const PROP_SIZES = {
    desk: [3.4, 0.82, 1.9],
    cubicle: [3.6, 1.9, 0.7],
    rack: [1.8, 3.2, 1.6],
    screens: [4.2, 2.9, 0.8],
    bale: [2.0, 1.4, 2.0],
    plant: [1.3, 1.6, 1.3],
    cooler: [1.0, 1.83, 1.0],
    coins: [1.4, 0.62, 1.4],
    chair: [1.1, 1.15, 1.1],
};
/**
 * The nearest quarter turn, without trigonometry.
 *
 * Collision is an axis-aligned box, so a quarter turn is the only rotation that
 * can be represented at all. Deriving it from `Math.atan2` and rounding would
 * work almost always and disagree between two engines at the boundaries —
 * and this value decides which way a prop's footprint runs, so a disagreement
 * is a client and server colliding against different boxes. Comparisons are
 * exact on every engine; atan2 is not.
 *
 * Returns radians: 0 faces -z, PI/2 faces -x, PI faces +z, -PI/2 faces +x.
 */
export function facingCenter(x, z) {
    if (Math.abs(x) > Math.abs(z))
        return x > 0 ? -Math.PI / 2 : Math.PI / 2;
    return z > 0 ? Math.PI : 0;
}
//# sourceMappingURL=props.js.map